import { VirtualMcSettings } from '@/src/components/events/eventDetailsSections/Summary/VirtualMcSettings';
import { config } from '@/src/config/app-config';
import { EditEventActions, useEditEvent } from '@/src/store/edit-event.context';
import { VirtualMcSource } from '@/src/utils/engagement/engagement-tracker';
import { EventFields, eventTitleFieldValidater } from '@/src/utils/event.validation.utils';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { Container, Header, SpaceBetween } from '@amzn/awsui-components-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Event } from '../../../types/Event';
import TargetSummaryDetails from '../../common/CampaignEventComponents/TargetSummaryDetails';
import EventDetailsNotes from '../eventDetailsSections/Summary/EventDetailsNotes';
import EventTimes from '../eventDetailsSections/Summary/EventTimes';
import PrivacyType from '../eventDetailsSections/Summary/PrivacyType';
import { useApi } from '@/src/store/api.context';
import { useUser } from '@/src/store/user.context';
import { EventAudienceType } from '@/src/constants/shared';
import AITeammateSettings from '@/src/components/events/eventDetailsSections/Summary/AITeammateSettings';

interface NewEventDetailsProps {
  target: Event;
  validationHandler: (validateSection: () => Promise<boolean>) => void;
  eventQuotaDaysPeriod?: number;
}

const EVENT_QUOTAS_PER_CHANNEL = {
  [EventAudienceType.SKILL_BUILDER_EVENT]: 100,
  [EventAudienceType.INTERNAL]: 100,
  [EventAudienceType.SKILL_BUILDER_LEARNER_EVENT]: 60,
};

const EVENT_QUOTA_DEFAULT = 30;
const EVENT_FETCH_LIMIT = 150;

const getEventQuotaPerChannel = (audienceType: string | null) => {
  if (!audienceType) return EVENT_QUOTA_DEFAULT;
  if (audienceType in EVENT_QUOTAS_PER_CHANNEL) {
    return EVENT_QUOTAS_PER_CHANNEL[audienceType];
  }
  return EVENT_QUOTA_DEFAULT;
};

const NewEventDetails: React.FC<NewEventDetailsProps> = ({ target, validationHandler, eventQuotaDaysPeriod = 30 }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { eventsApi } = useApi();
  const [titleError, setTitleError] = useState('');
  const [channelError, setChannelError] = useState('');
  const { handleUpdateEditEvent } = useEditEvent();

  const [requesterEvents, setRequesterEvents] = useState<Event[] | undefined>();

  useEffect(() => {
    if (!user?.isSuperAdmin && user?.email) {
      eventsApi
        .getEventsByDateRange({
          start: new Date(Date.now() - 24 * 3600 * eventQuotaDaysPeriod * 1000).toISOString(),
          end: new Date().toISOString(),
          includeEndedEvents: true,
          limit: EVENT_FETCH_LIMIT,
        })
        .then((results) => setRequesterEvents(results.filter((ev) => ev.requestedBy === user.email)))
        .catch((err) => {
          console.error('Failed to fetch requester events:', err);
          // Leave requesterEvents as undefined so the UI can
          // distinguish "failed to load" from "no events found"
          setRequesterEvents(undefined);
        });
    }
  }, [user?.email, user?.isSuperAdmin, eventsApi, eventQuotaDaysPeriod]);

  const isEvent = target instanceof Event;
  // this will validate the field and show the message
  const eventTitleValidation = useMemo(
    () =>
      eventTitleFieldValidater(
        target?.title ?? '',
        `${
          isEvent
            ? t(i18nKeys.events.fields.eventTitle.title)
            : t(i18nKeys.campaigns.labels.campaignDetails.campaignTitle)
        } ${t(i18nKeys.generic.lengthLimit4to250)}`,
        target?.audienceType,
        t(i18nKeys.newEvent.errors.audience),
        new Map<EventFields, (error: string) => void>([
          [EventFields.EVENT_TITLE, (error: string) => setTitleError(error)],
          [EventFields.EVENT_CHANNEL, (error: string) => setChannelError(error)],
        ])
      ),
    [target?.title, target?.audienceType, eventTitleFieldValidater]
  );

  const checkValidSettings = {
    virtualMcSettings: useRef(() => true),
    eventTimes: useRef(() => true),
  };

  validationHandler(() => {
    const titleValid = eventTitleValidation.isValidSection(true);
    const validSettings = Object.values(checkValidSettings).every((check) => check.current());

    return titleValid && validSettings ? Promise.resolve(true) : Promise.reject(false);
  });

  return (
    <SpaceBetween direction="vertical" size="s">
      <TargetSummaryDetails
        target={target}
        eventTitleValidation={eventTitleValidation}
        titleError={titleError}
        channelError={channelError}
      />
      <PrivacyType target={target} />
      <Container
        data-testid="event-times-expandable-section"
        header={<Header variant="h2">{t(i18nKeys.events.eventDetails.headers.eventTimes)}</Header>}
      >
        <EventTimes
          target={target}
          validatorRef={checkValidSettings.eventTimes}
          requesterEventsCount={requesterEvents?.filter((ev) => ev.audienceType === target.audienceType).length}
          eventQuotaDaysPeriod={eventQuotaDaysPeriod}
          eventQuotaLimit={getEventQuotaPerChannel(target.audienceType)}
          eventQuotaWarning={getEventQuotaPerChannel(target.audienceType) - 5}
        />
      </Container>
      {config.ff.enableVirtualMc && (
        <VirtualMcSettings
          virtualMcEnabled={target.virtualMcEnabled}
          setVirtualMcEnabled={(enabled) => handleUpdateEditEvent(EditEventActions.VIRTUAL_MC_ENABLED, enabled)}
          virtualMcVoice={target.virtualMcVoice}
          setVirtualMcVoice={(voice) => handleUpdateEditEvent(EditEventActions.VIRTUAL_MC_VOICE, voice)}
          virtualMcFrequency={target.virtualMcFrequency}
          setVirtualMcFrequency={(frequency) => handleUpdateEditEvent(EditEventActions.VIRTUAL_MC_FREQUENCY, frequency)}
          checkValid={checkValidSettings.virtualMcSettings}
          setDefaults
          source={VirtualMcSource.EVENT_CREATION}
        />
      )}
      {config.ff.enableAiTeammate && user?.isSuperAdmin && (
        <AITeammateSettings
          disabled={false}
          target={target}
          setAiTeammateEnabled={(enabled) => handleUpdateEditEvent(EditEventActions.AI_TEAMMATE_ENABLED, enabled)}
        />
      )}
      <EventDetailsNotes target={target} />
    </SpaceBetween>
  );
};

export default NewEventDetails;
